import { Core, Decorative } from "@faire/design-tokens";
import { FormattedText } from "@faire/retailer-visitor-shared/components/FormattedText";
import { VolumeBasedDiscounts } from "@faire/web--source/slate/icons/__generated__/icons";
import { Row } from "@faire/web--source/slate/Layout";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackPromotionalEventsPdpPromoBannerClick } from "@faire/web-api--source/events/promotionalEvents/click/pdpPromoBanner";
import { trackPromotionalEventsPdpPromoBannerView } from "@faire/web-api--source/events/promotionalEvents/view/pdpPromoBanner";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

interface IProps {
  promotions?: IInformationMessage[];
  position?: keyof typeof IInformationMessage.Position;
}

export const SimpleProductPromo: React.FC<IProps> = (props: IProps) => {
  const [tooltipIndex, setTooltipIndex] = useState(-1);

  const toggleTooltip = React.useCallback((index: number) => {
    setTooltipIndex((oldIndex) => (oldIndex === index ? -1 : index));
  }, []);

  if ((props.promotions?.length ?? 0) === 0) {
    return null;
  }

  const filteredPromotions = props.promotions?.filter((promo) => {
    const bannerPosition =
      promo.position ?? IInformationMessage.Position.DEFAULT;
    return (
      promo.type !== IInformationMessage.Type.CATEGORY_RESTRICTION &&
      bannerPosition === props.position
    );
  });

  return (
    <>
      {filteredPromotions?.map((productPromotion, index) => (
        <SimplePromotionBanner
          key={index}
          productPromotion={productPromotion}
          index={index}
          toggleTooltip={toggleTooltip}
          tooltipIndex={tooltipIndex}
        />
      ))}
    </>
  );
};

const SimplePromotionBanner: React.FC<{
  productPromotion: IInformationMessage;
  index: number;
  toggleTooltip: (index: number) => void;
  tooltipIndex: number;
}> = ({ productPromotion, index, toggleTooltip, tooltipIndex }) => {
  const viewRef = useDoActionWhenVisible(() =>
    trackPromotionalEventsPdpPromoBannerView(
      productPromotion.type ?? IInformationMessage.Type.TYPE_UNKNOWN
    )
  );
  const renderTooltip = useCallback(
    (index: number, productPromotion: IInformationMessage) => {
      return (
        <>
          &nbsp;
          <Tooltip
            isOpen={tooltipIndex === index}
            innerClassName="tooltip-inner"
            pointerOffset={130}
          >
            {productPromotion.tooltip}
            {productPromotion.help_link ? (
              <EmbeddedLink
                target="_blank"
                linkTo={productPromotion.help_link}
                color={Core.text.inverse}
                onClick={() => {
                  trackPromotionalEventsPdpPromoBannerClick(
                    productPromotion.type ??
                      IInformationMessage.Type.TYPE_UNKNOWN
                  );
                  toggleTooltip(index);
                }}
              >
                <StrictLocalMsg defaultMessage="Learn More" />
              </EmbeddedLink>
            ) : null}
          </Tooltip>
        </>
      );
    },
    [toggleTooltip, tooltipIndex]
  );

  const isVolumePricingPromo =
    productPromotion.type === IInformationMessage.Type.VOLUME_PRICING;

  const textColor = isVolumePricingPromo
    ? Core.text.default
    : Decorative.shade.orange;

  return (
    <SimplePromotionContainer
      ref={viewRef}
      onClick={() => toggleTooltip(index)}
      data-test-id={`simpleProductPromo:${productPromotion.type}`}
      className={isVolumePricingPromo ? "mt-2" : undefined}
    >
      <Typography color={textColor}>
        {isVolumePricingPromo ? (
          <span className="relative -top-[1px] mr-1">
            <VolumeBasedDiscounts width="10px" />
          </span>
        ) : null}
        <FormattedText text={productPromotion.text} />
        {productPromotion.tooltip
          ? renderTooltip(index, productPromotion)
          : null}
      </Typography>
    </SimplePromotionContainer>
  );
};

const SimplePromotionContainer = styled(Row).attrs({
  align: "flex-start",
})`
  width: 100%;
`;
